import { Copy } from "lucide-react";
import { useCallback, useState } from "react";
import { copyToClipboard } from "../services/share";
import { Button } from "~/components/ui/button";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "~/components/ui/dialog";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "~/components/ui/tooltip";

interface DonateModalProps {
  onClose?: () => void;
}

export function DonateModal({ onClose }: DonateModalProps) {
  const [showCopied, setShowCopied] = useState(false);

  // Multi-language content
  const content = {
    "uz-Latn": {
      title: "Ixtiyoriy yordam",
      description:
        "Saytimiz sizga yoqayaptimi? Undanam yaxshi bo'lishini xohlaysizmi? Lotin.uz saytini yanada takomillashtirish uchun qo'llab-quvvatlang!",
      cardNumber: "5614 6825 1643 3986",
      closeButton: "Yopish",
      copied: "Nusxalandi!",
    },
    "uz-Cyrl": {
      title: "Ихтиёрий ёрдам",
      description:
        "Сайтимиз сизга ёқаяптими? Ундаям яхши бўлишини хоҳлайсизми? Lotin.uz сайтини янада такомиллаштириш учун қўллаб-қувватланг!",
      cardNumber: "5614 6825 1643 3986",
      closeButton: "Ёпиш",
      copied: "Нусхаланди!",
    },
    ru: {
      title: "Добровольная помощь",
      description:
        "Вам нравится наш сайт? Хотите, чтобы он стал еще лучше? Поддержите Lotin.uz для дальнейшего улучшения!",
      cardNumber: "5614 6825 1643 3986",
      closeButton: "Закрыть",
      copied: "Скопировано!",
    },
    "en-US": {
      title: "Voluntary Support",
      description:
        "Do you like our site? Want it to be even better? Support Lotin.uz for further improvement!",
      cardNumber: "5614 6825 1643 3986",
      closeButton: "Close",
      copied: "Copied!",
    },
  };

  // Language detection (same pattern as Footer/AboutCard)
  const getLanguage = () => {
    if (typeof window === "undefined") return "oz";
    const segments = window.location.pathname.split("/").filter(Boolean);
    const langs = ["uz", "ru", "en"];
    return langs.includes(segments[0]) ? segments[0] : "oz";
  };

  const langMap: Record<string, keyof typeof content> = {
    oz: "uz-Latn",
    uz: "uz-Cyrl",
    ru: "ru",
    en: "en-US",
  };

  const currentLang = getLanguage();
  const currentContent = content[langMap[currentLang]] || content["uz-Latn"];

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(currentContent.cardNumber);
    if (success) {
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    }
  }, [currentContent.cardNumber]);

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{currentContent.title}</DialogTitle>
        <DialogDescription>{currentContent.description}</DialogDescription>
      </DialogHeader>
      <div className="flex items-center justify-between bg-emerald-100 border border-emerald-300 rounded-xl p-4">
        <span className="text-2xl font-mono font-bold text-gray-900">
          {currentContent.cardNumber}
        </span>
        <Tooltip open={showCopied}>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleCopy}>
              <Copy className="w-6 h-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{currentContent.copied}</TooltipContent>
        </Tooltip>
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">{currentContent.closeButton}</Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
}
